import { Injectable } from '@angular/core';
import { FlowTypes } from '@interfaces/models/flow-types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { LoggerCodes } from '@shared/modules/logger/constants/logger.constants';
import { WindowRef } from '@shared/modules/window-ref/window-ref';
import { DocumentsService } from '@shared/services/documents/documents.service';
import { ViadocService } from '@shared/services/viadoc/viadoc.service';
import { StopPendingEffectsService } from '@src/app/core/services/stop-pending-effects.service';
import { getFileDescriptorsFromMetadata } from '@src/app/secure/document-upload/helpers';
import { mergeFilesToPdf } from '@src/app/secure/document-upload/helpers/merge-files-to-pdf.helper';
import { appianSubmit, internalTransferAppianSubmit, LogError, LogInfo } from '@store/actions';
import * as fromRootStore from '@store/index';
import { selectViadocScanPlan, ViadocScanSlice } from '@store/selectors/viadoc/viadoc.selectors';
import { error } from 'console';
import {
  catchError,
  EMPTY,
  filter,
  from,
  map,
  merge,
  Observable,
  of,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

@Injectable()
export class ViadocEffects {
  scanUploadedDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appianSubmit, internalTransferAppianSubmit),
      withLatestFrom(this.store$.pipe(select(selectViadocScanPlan))),
      filter(([, plan]) => plan.toggleOn && plan.slices.length > 0),
      switchMap(([, plan]) =>
        merge(...plan.slices.map((slice) => this.scanSlice(slice, plan.flowType))),
      ),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private viadocService: ViadocService,
    private documentsService: DocumentsService,
    private windowRef: WindowRef,
    private stopPendingEffectsService: StopPendingEffectsService,
  ) {}

  private scanSlice(slice: ViadocScanSlice, flowType: FlowTypes | null): Observable<Action> {
    return from(this.buildMergedPdfBlob(slice)).pipe(
      switchMap((blob) =>
        blob === null
          ? EMPTY
          : this.viadocService.scanDocument(blob, {
              expectedTypes: slice.expectedTypes,
              uploadStep: slice.uploadStep,
              flowType,
            }),
      ),
      map((response) =>
        LogInfo({
          payload: {
            eventId: 'viadoc-shadow-scan-outcome',
            uploadStep: slice.uploadStep,
            flowType,
            expectedTypes: slice.expectedTypes,
            detectedTypes: response.detectedTypes,
            missingTypes: response.missingTypes,
            valid: response.valid,
            code: LoggerCodes.GENERAL_SUCCESS,
          },
        }),
      ),
      catchError((error: unknown) =>
        of(
          LogError({
            payload: {
              eventId: 'viadoc-shadow-scan-error',
              uploadStep: slice.uploadStep,
              flowType,
              expectedTypes: slice.expectedTypes,
              message: String(error),
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        ),
      ),
    );
  }

  private async buildMergedPdfBlob(slice: ViadocScanSlice): Promise<Blob | null> {
    const fileDescriptors = getFileDescriptorsFromMetadata(slice.files);

    const files = fileDescriptors.length
      ? await this.documentsService.rehydrateFiles(slice.files, fileDescriptors)
      : slice.files;

    if (files.length === 0) {
      return null;
    }

    const pdfDoc = await mergeFilesToPdf({
      files,
      fetchFn: (url: string) => this.windowRef.nativeWindow.fetch(url),
    });

    const bytes = await pdfDoc.save();
    return new Blob([bytes], { type: 'application/pdf' });
  }
}
